import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    try {
      const created = await signup(name, email, password);
      const welcome = Number(created?.credits) || 0;
      toast.success('Welcome to Radiance Hub', {
        description: welcome > 0 ? `You got ${welcome} free credits to try AI features` : undefined,
      });
      navigate('/profile', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sign up failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="signup-page" className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="relative hidden lg:block bg-cream-paper">
        <img
          src="https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=1200&q=80"
          alt="Skincare ritual"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-cream-paper/30 via-transparent to-cream-paper/40" />
        <div className="relative h-full flex items-end p-10">
          <blockquote className="max-w-md text-ink">
            <p className="font-display text-3xl leading-tight">
              “Your skin tells your story. Curate the chapters.”
            </p>
            <span className="mt-3 block text-xs uppercase tracking-[0.22em] text-rose-gold">Radiance Hub</span>
          </blockquote>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md bg-card border-border/70 rounded-2xl p-7 sm:p-8">
          <div className="flex justify-center mb-6">
            <BrandLogo size={56} variant="stacked" showTagline />
          </div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-5 w-5 text-rose-gold" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold">Begin your ritual</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            Create your beauty profile.
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            One minute now. Tailored hacks &amp; products forever.{' '}
            <span className="inline-flex items-center gap-1 text-rose-gold font-medium">
              <Sparkles className="h-3 w-3" /> 3 free credits on signup
            </span>
          </p>

          <form onSubmit={handle} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Display name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="What should we call you?"
                className="mt-2 h-11 rounded-xl bg-card"
                data-testid="signup-name-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@radiance.com"
                className="mt-2 h-11 rounded-xl bg-card"
                data-testid="signup-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="At least 6 characters"
                className="mt-2 h-11 rounded-xl bg-card"
                data-testid="signup-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 h-11"
              data-testid="signup-submit-button"
            >
              {submitting ? 'Creating...' : 'Create my profile'}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            Already a member?{' '}
            <Link to="/login" className="text-foreground underline underline-offset-4" data-testid="link-to-login">
              Sign in
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
